import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Building2, Check, ChevronsUpDown, FolderKanban, LogIn, Plus, Settings2, User } from 'lucide-react';
import { WORKSPACE_COLORS, type WorkspaceSummary } from '@mailpilot/shared';
import { Button, ChipInput, Field, Input, InlineError, Menu, Modal } from '../ui';
import { useCreateOrganization, useCurrentWorkspace, useJoinOrganization, useOpenPersonalWorkspace, useSwitchWorkspace } from '../../hooks/useWorkspace';

export function WorkspaceMark({ ws, size = 36 }: { ws: Pick<WorkspaceSummary, 'name' | 'color' | 'kind'>; size?: number }) {
  return (
    <span
      aria-hidden
      className="flex shrink-0 items-center justify-center rounded-[11px] font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]"
      style={{ width: size, height: size, background: `linear-gradient(135deg, ${ws.color}, ${ws.color}cc)`, fontSize: Math.round(size * 0.42) }}
    >
      {ws.kind === 'personal' ? <User style={{ width: size * 0.5, height: size * 0.5 }} /> : ws.name.trim().charAt(0).toUpperCase()}
    </span>
  );
}

const roleLabel = (ws: WorkspaceSummary) =>
  ws.kind === 'personal' ? 'Personal workspace' : `${ws.role === 'owner' ? 'Owner' : ws.role === 'admin' ? 'Admin' : 'Member'} · ${ws.memberCount} member${ws.memberCount === 1 ? '' : 's'}`;

export function CreateOrganizationModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState('');
  const [divisions, setDivisions] = useState<string[]>([]);
  const [color, setColor] = useState(WORKSPACE_COLORS[0]);
  const create = useCreateOrganization();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) create.mutate({ name: name.trim(), color, divisions }, { onSuccess: onClose });
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create an organization"
      description="Share email accounts with your team. Divisions let you keep some accounts to the people who need them."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="create-org" loading={create.isPending} disabled={!name.trim()}>
            Create organization
          </Button>
        </>
      }
    >
      <form id="create-org" onSubmit={submit} className="space-y-4">
        <Field label="Organization name">{(id) => <Input id={id} data-autofocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Inc." maxLength={80} />}</Field>
        <Field label="Colour">
          {() => (
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Organization colour">
              {WORKSPACE_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  role="radio"
                  aria-checked={color === c}
                  aria-label={c}
                  onClick={() => setColor(c)}
                  className={clsx('h-8 w-8 rounded-full transition-transform hover:scale-110', color === c && 'ring-2 ring-offset-2 ring-offset-surface')}
                  style={{ background: c, ['--tw-ring-color' as string]: c }}
                />
              ))}
            </div>
          )}
        </Field>
        <Field label="Divisions (optional)" hint="Teams such as Sales or Support. You can add or change these later.">
          {(id) => <ChipInput id={id} label="Divisions" value={divisions} onChange={setDivisions} placeholder="Type a name and press Enter" />}
        </Field>
        <InlineError error={create.error} />
      </form>
    </Modal>
  );
}

export function JoinOrganizationModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [code, setCode] = useState('');
  const join = useJoinOrganization();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (code.trim()) join.mutate(code.trim(), { onSuccess: onClose });
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Join an organization"
      description="Enter the invite code (or paste the invite link) an admin shared with you."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="join-org" loading={join.isPending} disabled={code.trim().length < 4}>
            Join
          </Button>
        </>
      }
    >
      <form id="join-org" onSubmit={submit} className="space-y-4">
        <Field label="Invite code">
          {(id) => <Input id={id} data-autofocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="ABCD-EFGH-JKLM" autoCapitalize="characters" className="font-mono tracking-wider" />}
        </Field>
        <InlineError error={join.error} />
      </form>
    </Modal>
  );
}

/** Which workspace you're in, and switching / creating / joining others. Rendered as a pill in the top bar. */
export function WorkspaceSwitcher({ compact }: { compact?: boolean }) {
  const navigate = useNavigate();
  const { workspace, workspaces, isOrg } = useCurrentWorkspace();
  const switchWs = useSwitchWorkspace();
  const openPersonal = useOpenPersonalWorkspace();
  const [dialog, setDialog] = useState<'create' | 'join' | null>(null);
  const inProjects = useLocation().pathname.startsWith('/projects');
  if (!workspace) return null;
  const hasPersonal = workspaces.some((w) => w.kind === 'personal');

  return (
    <>
      <Menu
        label="Workspaces"
        width={288}
        header={<p className="px-3 pb-1 pt-2 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">Workspaces</p>}
        trigger={(p) => (
          <button
            type="button"
            {...p}
            aria-label={`Workspace: ${workspace.name}. Switch workspace`}
            className={clsx(
              'group flex h-10 items-center gap-2.5 rounded-full border border-hairline bg-surface/80 pl-1.5 pr-3 text-left shadow-sm transition-[box-shadow,background-color] hover:bg-surface hover:shadow-card',
              compact && 'pr-1.5',
            )}
          >
            <WorkspaceMark ws={workspace} size={28} />
            {!compact && (
              <span className="min-w-0">
                <span className="block max-w-[11rem] truncate text-[0.8125rem] font-semibold leading-tight text-ink">{workspace.name}</span>
                <span className="block truncate text-[0.625rem] font-medium uppercase tracking-wider text-subtle">{inProjects ? 'Projects' : workspace.kind === 'personal' ? 'Personal' : workspace.role}</span>
              </span>
            )}
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted transition-transform group-hover:scale-110" />
          </button>
        )}
        items={[
          ...workspaces.map((w) => ({
            key: w.id,
            label: w.name,
            description: roleLabel(w),
            icon: <WorkspaceMark ws={w} size={28} />,
            active: w.id === workspace.id,
            trailing: w.id === workspace.id ? <Check className="h-4 w-4" /> : undefined,
            onSelect: () => w.id !== workspace.id && switchWs.mutate(w.id),
          })),
          'divider' as const,
          ...(isOrg ? [{ key: 'projects', label: 'Projects', description: 'Mail grouped by client, deal or job', icon: <FolderKanban className="h-4 w-4" />, onSelect: () => navigate('/projects') }] : []),
          ...(isOrg ? [{ key: 'org', label: 'Organization & team', icon: <Settings2 className="h-4 w-4" />, onSelect: () => navigate('/settings/organization') }] : []),
          { key: 'create', label: 'Create an organization…', icon: <Plus className="h-4 w-4" />, onSelect: () => setDialog('create') },
          { key: 'join', label: 'Join with an invite code…', icon: <LogIn className="h-4 w-4" />, onSelect: () => setDialog('join') },
          ...(!hasPersonal ? [{ key: 'personal', label: 'Open a personal workspace', icon: <Building2 className="h-4 w-4" />, onSelect: () => openPersonal.mutate() }] : []),
        ]}
      />
      <CreateOrganizationModal open={dialog === 'create'} onClose={() => setDialog(null)} />
      <JoinOrganizationModal open={dialog === 'join'} onClose={() => setDialog(null)} />
    </>
  );
}
